import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import logger from '../utils/logger';
import { Phone, Clock, Shield, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import QuickSetupCalendar from './onboarding/QuickSetupCalendar';
import useDirtyConfirm from '../hooks/useDirtyConfirm';
import InlineConfirmCloseBar from './ui/InlineConfirmCloseBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuickSetupCallModal({ isOpen, onClose, onSuccess }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [maskedMode, setMaskedMode] = useState(true);
  const [notes, setNotes] = useState('');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [dateError, setDateError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);

  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const token = localStorage.getItem('auth_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (isOpen) { setStep(0); setSelectedDate(''); setSelectedTime(''); setMaskedMode(true); setNotes(''); }
  }, [isOpen]);

  const loadSlots = useCallback(async (dateStr) => {
    setSlotsLoading(true); setDateError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/available-slots?date_str=${dateStr}&session_type=quick_setup`, { headers, cache: 'no-store' });
      const data = await res.json();
      if (!data.available) { setDateError(data.reason); setSlots([]); }
      else { setSlots(data.slots); }
    } catch (err) { logger.error(err); setDateError('Failed to load slots'); }
    finally { setSlotsLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDateClick = (dateStr) => { setSelectedDate(dateStr); setSelectedTime(''); loadSlots(dateStr); };

  // Discard guard — dirty as soon as the user picks a date/time or types notes.
  const isDirty = !!(selectedDate || selectedTime || notes.trim());
  const { showConfirm, requestClose, confirmDiscard, cancelDiscard } = useDirtyConfirm({ onClose, isDirty });

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/quick-setup/request`, {
        method: 'POST', headers,
        body: JSON.stringify({ scheduled_date: selectedDate, scheduled_time: selectedTime, masked_mode: maskedMode, notes: notes || null })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      onSuccess?.(await res.json());
      onClose();
    } catch (err) { alert(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) requestClose(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="quick-setup-modal"
        onEscapeKeyDown={(e) => { e.preventDefault(); requestClose(); }}
        onInteractOutside={(e) => { e.preventDefault(); requestClose(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Phone className="w-5 h-5 text-blue-600" />Quick Virtual Setup Session</DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-1">A 10-15 minute guided call to help you get started</DialogDescription>
        </DialogHeader>
        {showConfirm && (
          <InlineConfirmCloseBar onDiscard={confirmDiscard} onKeep={cancelDiscard} />
        )}
        {step === 0 && (
          <div className="space-y-4" data-testid="quick-step-schedule">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
              <Clock className="w-5 h-5 text-blue-600 shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-800">Complimentary 10-15 min session</p>
                <p className="text-xs text-blue-600">Pick a date and time that works for you</p>
              </div>
            </div>
            <QuickSetupCalendar calendarMonth={calendarMonth} setCalendarMonth={setCalendarMonth} selectedDate={selectedDate} onDateClick={handleDateClick} />
            {dateError && <p className="text-xs text-red-500">{dateError}</p>}
            {selectedDate && slots.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-2"><Clock className="w-3 h-3 inline mr-1" />Available slots</p>
                <div className="grid grid-cols-4 gap-2">
                  {slots.map(s => (
                    <button key={s.time} onClick={() => setSelectedTime(s.time)}
                      className={`text-xs py-2 px-1 rounded-md border transition-colors
                        ${selectedTime === s.time ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 hover:border-blue-400 text-slate-600'}`}
                      data-testid={`quick-slot-${s.time}`}>{s.label}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4" data-testid="quick-step-confirm">
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Date</span><span className="font-medium">{selectedDate}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Time</span><span className="font-medium">{selectedTime} (10-15 min)</span></div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-600" /><span className="text-sm">Masked Mode</span></div>
                <Switch checked={maskedMode} onCheckedChange={setMaskedMode} data-testid="quick-masked-toggle" />
              </div>
              {maskedMode && <p className="text-xs text-slate-400">Your data will be hidden from the support assistant. Only structure and field inputs will be visible.</p>}
            </div>
          </div>
        )}
        <DialogFooter className="flex gap-2">
          {step > 0 && <Button variant="outline" onClick={() => setStep(0)} data-testid="quick-back-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>}
          <div className="flex-1" />
          {step === 0 ? (
            <Button onClick={() => setStep(1)} disabled={!selectedDate || !selectedTime} className="bg-blue-600 hover:bg-blue-700" data-testid="quick-next-btn">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="quick-submit-btn">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Book Call</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
